import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    listEvents,
    getEvent,
    joinEvent,
    leaveEvent,
    listBusinessEvents,
    listEventSessions,
} from '../api/events-service';

export interface Event {
    id: string;
    name: string;
    detail?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    formatId?: number;
    venueName?: string;
    address?: string;
    capacity?: number;
    isPaid?: boolean;
    ticketPrice?: number;
    currency?: string;
    timeZone?: string;
    business?: {
        name: string;
    };
    organizers?: Array<{ name: string }>;
}

export interface EventSession {
    id: string;
    sessionTitle?: string;
    startTime?: string;
    endTime?: string;
    space?: {
        name: string;
    };
    note?: string;
}

interface EventsState {
    events: Event[];
    selectedEvent: Event | null;
    eventSessions: EventSession[];
    joinedEvents: Event[];
    isLoading: boolean;
    isDetailLoading: boolean;
    sessionsLoading: boolean;
    joinedLoading: boolean;
    error: string | null;
}

const initialState: EventsState = {
    events: [],
    selectedEvent: null,
    eventSessions: [],
    joinedEvents: [],
    isLoading: false,
    isDetailLoading: false,
    sessionsLoading: false,
    joinedLoading: false,
    error: null,
};

// Async thunks
export const fetchEvents = createAsyncThunk(
    'events/fetchEvents',
    async (params: { status?: string; public?: boolean } = {}) => {
        const response = await listEvents(params);
        return response.data;
    }
);

export const fetchEventById = createAsyncThunk(
    'events/fetchEventById',
    async ({ id, params }: { id: string; params?: { public?: boolean } }) => {
        const response = await getEvent(id, params);
        return response.data;
    }
);

export const fetchEventSessions = createAsyncThunk(
    'events/fetchEventSessions',
    async ({ eventId, params }: { eventId: string; params?: { public?: boolean } }) => {
        const response = await listEventSessions(eventId, params);
        return response.data;
    }
);

export const fetchBusinessEvents = createAsyncThunk(
    'events/fetchBusinessEvents',
    async (businessId: string) => {
        const response = await listBusinessEvents(businessId);
        return response.data;
    }
);

export const joinEventAction = createAsyncThunk(
    'events/joinEvent',
    async ({ eventId, businessId }: { eventId: string; businessId: string }) => {
        await joinEvent(eventId, businessId);
        return { eventId, businessId };
    }
);

export const leaveEventAction = createAsyncThunk(
    'events/leaveEvent',
    async ({ eventId, businessId }: { eventId: string; businessId: string }) => {
        await leaveEvent(eventId, businessId);
        return { eventId, businessId };
    }
);

const eventsSlice = createSlice({
    name: 'events',
    initialState,
    reducers: {
        setSelectedEvent: (state, action: PayloadAction<Event | null>) => {
            state.selectedEvent = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch events
        builder
            .addCase(fetchEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                // FAZ 5 / F5-10 — `fetchAllPagesVia` her zaman `{ data: T[] }`
                // donduruyor (hem eski hem yeni backend seklini normalize
                // ediyor), yani eski savunmaci `?.data ?? ?.items` zinciri
                // artik olu kod.
                const data = action.payload;
                state.events = Array.isArray(data) ? data : [];
            })
            .addCase(fetchEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch events';
            });

        // Fetch event by ID
        builder
            .addCase(fetchEventById.pending, (state) => {
                state.isDetailLoading = true;
            })
            .addCase(fetchEventById.fulfilled, (state, action) => {
                state.isDetailLoading = false;
                const data = action.payload?.data ?? action.payload?.item ?? action.payload;
                state.selectedEvent = data;
            })
            .addCase(fetchEventById.rejected, (state, action) => {
                state.isDetailLoading = false;
                state.error = action.error.message || 'Failed to fetch event details';
            });

        // Fetch event sessions
        builder
            .addCase(fetchEventSessions.pending, (state) => {
                state.sessionsLoading = true;
            })
            .addCase(fetchEventSessions.fulfilled, (state, action) => {
                state.sessionsLoading = false;
                const data = action.payload?.data ?? action.payload?.items ?? action.payload;
                state.eventSessions = Array.isArray(data) ? data : [];
            })
            .addCase(fetchEventSessions.rejected, (state) => {
                state.sessionsLoading = false;
                state.eventSessions = [];
            });

        // Fetch business events
        builder
            .addCase(fetchBusinessEvents.pending, (state) => {
                state.joinedLoading = true;
            })
            .addCase(fetchBusinessEvents.fulfilled, (state, action) => {
                state.joinedLoading = false;
                const data = action.payload?.data ?? action.payload?.items ?? action.payload;
                state.joinedEvents = Array.isArray(data) ? data : [];
            })
            .addCase(fetchBusinessEvents.rejected, (state) => {
                state.joinedLoading = false;
                state.joinedEvents = [];
            });

        // Join event
        builder
            .addCase(joinEventAction.fulfilled, (state, action) => {
                // Refresh joined events list after joining
            })
            .addCase(joinEventAction.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to join event';
            });

        // Leave event
        builder
            .addCase(leaveEventAction.fulfilled, (state, action) => {
                // Remove event from joined list
                state.joinedEvents = state.joinedEvents.filter(
                    (event) => event.id !== action.payload.eventId
                );
            })
            .addCase(leaveEventAction.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to leave event';
            });
    },
});

export const { setSelectedEvent, clearError } = eventsSlice.actions;
export default eventsSlice.reducer;
